import { useCenterStore } from '@caradasstore/CenterStore'
import { useUserStore } from '@caradasstore'
import { AdasBox, AdasTypography } from '@components/wrapper-components'
import { Link2, Loader2 } from 'lucide-react'
import PropTypes from 'prop-types'
import { useEffect, useState } from 'react'
import { getCccConnection, updateCccConnection } from '../../../api/cccConnectionApi'
import { getFriendlyErrorMessage } from '../../../utils/errorMessage'

// Same rule as the backend (CccConnectionService): letters, digits and dashes, up to 64.
const CCC_ID = /^[A-Za-z0-9-]{1,64}$/

const inputClass =
  'h-10 w-full rounded-lg border border-input bg-card px-3 text-[14px] text-foreground focus:outline-none focus:ring-2 focus:ring-secondary/40'

/**
 * Settings → CCC connection: the CCC ONE Secure Share ID of this location's own body shop, so estimates shared from
 * CCC reach it. Works on every plan — AI Brief has no Clients page to set it on. Owners, managers and admins edit;
 * everyone else sees it read-only.
 */
export const CccConnectionCard = ({ ccId: ccIdProp }) => {
  const storeCcId = useCenterStore((store) => store.ccId)
  const ccId = ccIdProp ?? storeCcId
  const currentUser = useUserStore((store) => store.currentUser)
  const canEdit = !!(currentUser?.is_admin || currentUser?.is_manager || currentUser?.is_owner)

  const [state, setState] = useState({ status: 'loading', view: null })
  const [value, setValue] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    if (!ccId) return undefined
    let active = true
    setState({ status: 'loading', view: null })
    getCccConnection(ccId)
      .then((view) => {
        if (!active) return
        setState({ status: 'ready', view })
        setValue(view?.cccId || '')
      })
      .catch((err) => active && setState({ status: 'error', view: null, message: getFriendlyErrorMessage(err) }))
    return () => {
      active = false
    }
  }, [ccId])

  const current = state.view?.cccId || ''
  const trimmed = value.trim()
  const invalid = trimmed !== '' && !CCC_ID.test(trimmed)
  const unchanged = trimmed === current

  const save = async (event) => {
    event.preventDefault()
    if (invalid || unchanged) return
    setSaving(true)
    setError(null)
    setSaved(false)
    try {
      const view = await updateCccConnection(ccId, trimmed)
      setState({ status: 'ready', view })
      setValue(view?.cccId || '')
      setSaved(true)
    } catch (err) {
      setError(getFriendlyErrorMessage(err))
    } finally {
      setSaving(false)
    }
  }

  return (
    <AdasBox className='rounded-2xl border border-border bg-white p-5 shadow-sm' aria-label='CCC connection'>
      <AdasBox className='flex items-start gap-3'>
        <span className='inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-secondary/10'>
          <Link2 className='h-5 w-5 text-secondary' strokeWidth={1.75} />
        </span>
        <AdasBox className='min-w-0'>
          <AdasTypography className='text-[15px] font-semibold text-foreground'>CCC ONE Secure Share ID</AdasTypography>
          <AdasTypography className='mt-0.5 text-[13px] text-muted-foreground'>
            Estimates your shop shares from CCC ONE reach this account through this ID. Leave it empty to stop receiving them.
          </AdasTypography>
        </AdasBox>
      </AdasBox>

      <AdasBox className='mt-4'>
        {state.status === 'loading' && (
          <AdasBox className='flex items-center gap-2 text-[13px] text-muted-foreground' aria-busy='true'>
            <Loader2 className='h-4 w-4 animate-spin' /> Loading…
          </AdasBox>
        )}

        {state.status === 'error' && (
          <AdasTypography className='text-[13px] text-[#B42318]' role='alert'>
            {state.message}
          </AdasTypography>
        )}

        {state.status === 'ready' && !state.view?.available && (
          <AdasTypography className='text-[13px] text-muted-foreground'>{state.view?.reason}</AdasTypography>
        )}

        {state.status === 'ready' && state.view?.available && (
          <form onSubmit={save} noValidate>
            <label htmlFor='ccc-id' className='mb-1 block text-[12px] font-medium text-foreground'>
              CCC ID for {state.view.clientName}
            </label>
            {canEdit ? (
              <AdasBox className='flex flex-col gap-2 sm:flex-row sm:items-start'>
                <AdasBox className='w-full sm:max-w-[320px]'>
                  <input
                    id='ccc-id'
                    value={value}
                    onChange={(e) => {
                      setValue(e.target.value)
                      setSaved(false)
                      setError(null)
                    }}
                    maxLength={64}
                    placeholder='e.g. 43004'
                    autoComplete='off'
                    aria-invalid={invalid}
                    className={`${inputClass} ${invalid ? 'border-[#DC2626]' : ''}`}
                  />
                  {invalid && <AdasTypography className='mt-1 text-[12px] text-[#B42318]'>Letters, numbers and - only</AdasTypography>}
                </AdasBox>
                <button
                  type='submit'
                  disabled={saving || invalid || unchanged}
                  className='inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-sidebar px-4 text-[14px] font-semibold text-white transition-colors hover:bg-sidebar/90 disabled:cursor-not-allowed disabled:opacity-50'
                >
                  {saving && <Loader2 className='h-4 w-4 animate-spin' />}
                  {trimmed === '' && current ? 'Disconnect' : 'Save'}
                </button>
              </AdasBox>
            ) : (
              <AdasTypography id='ccc-id' className='text-[15px] font-semibold text-foreground'>
                {current || 'Not connected'}
              </AdasTypography>
            )}
            {error && (
              <AdasTypography className='mt-2 text-[13px] text-[#B42318]' role='alert'>
                {error}
              </AdasTypography>
            )}
            {saved && (
              <AdasTypography className='mt-2 text-[13px] text-[#16803F]' role='status'>
                {current ? `Connected: estimates shared from CCC ONE under ${current} now reach this account.` : 'Disconnected.'}
              </AdasTypography>
            )}
          </form>
        )}
      </AdasBox>
    </AdasBox>
  )
}
CccConnectionCard.propTypes = {
  /** Defaults to the active location. */
  ccId: PropTypes.number
}
